import { useEffect, useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { hasAnalyticsConsent } from '../lib/privacyConsent';

const API = process.env.REACT_APP_BACKEND_URL;
const CAMPAIGN_SESSION_KEY = 'rastyle_campaign_attribution';

// Generate or get persistent visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('rastyle_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('rastyle_visitor_id', visitorId);
  }
  return visitorId;
};

// Generate session ID (new each browser session)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('rastyle_session_id');
  if (!sessionId) {
    sessionId = 's_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('rastyle_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Get browser name
const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('SamsungBrowser')) return 'Samsung Browser';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  if (ua.includes('Trident')) return 'IE';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Unknown';
};

// Get OS
const getOS = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
};

// Parse UTM parameters
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  const current = {
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
    utm_content: params.get('utm_content') || null,
    utm_term: params.get('utm_term') || null,
    landing_page: window.location.pathname,
  };
  const hasCampaign = Object.entries(current).some(([key, value]) => key !== 'landing_page' && value);
  try {
    if (hasCampaign) {
      sessionStorage.setItem(CAMPAIGN_SESSION_KEY, JSON.stringify(current));
      return current;
    }
    return JSON.parse(sessionStorage.getItem(CAMPAIGN_SESSION_KEY) || 'null') || current;
  } catch {
    return current;
  }
};

// Get page type from URL
const getPageType = (pathname) => {
  if (pathname === '/' || pathname === '/home') return 'home';
  if (pathname.startsWith('/events')) return 'events';
  if (pathname.startsWith('/mixtapes')) return 'mixtapes';
  if (pathname.startsWith('/creators')) return 'creators';
  if (pathname.startsWith('/shop')) return 'shop';
  if (pathname.startsWith('/daily-challenge') || pathname.startsWith('/trivia')) return 'trivia';
  if (pathname.startsWith('/dashboard')) return 'dashboard';
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/login') || pathname.startsWith('/register')) return 'auth';
  return 'other';
};

const AnalyticsTracker = () => {
  const location = useLocation();
  const [consentGranted, setConsentGranted] = useState(hasAnalyticsConsent);
  const lastPageRef = useRef(null);

  useEffect(() => {
    const onConsent = (event) => {
      const granted = event.detail?.analytics === true;
      setConsentGranted(granted);
      if (!granted) {
        localStorage.removeItem('rastyle_visitor_id');
        sessionStorage.removeItem(CAMPAIGN_SESSION_KEY);
        lastPageRef.current = null;
      }
    };
    window.addEventListener('cookieConsentUpdate', onConsent);
    return () => window.removeEventListener('cookieConsentUpdate', onConsent);
  }, []);

  // Track event
  const trackEvent = useCallback(async (eventType, additionalData = {}) => {
    if (!consentGranted) return;
    const pageType = getPageType(window.location.pathname);
    if (pageType === 'admin' || pageType === 'auth') return;
    try {
      const baseData = {
        event_type: eventType,
        consent_granted: true,
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        page_url: window.location.pathname,
        page_title: document.title,
        page_type: pageType,
        device_type: getDeviceType(),
        browser: getBrowser(),
        os: getOS(),
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        ...getUTMParams(),
        ...additionalData
      };

      await axios.post(`${API}/api/analytics/track`, baseData);
    } catch (err) {
      // Silently fail - analytics shouldn't break the app
      console.debug('Analytics error:', err);
    }
  }, [consentGranted]);

  // Track page views
  useEffect(() => {
    if (consentGranted && lastPageRef.current !== location.pathname) {
      trackEvent('pageview');
      lastPageRef.current = location.pathname;
    }
  }, [consentGranted, location.pathname, trackEvent]);

  // Track clicks on important elements
  useEffect(() => {
    const handleClick = (e) => {
      if (!consentGranted) return;
      const target = e.target.closest('[data-analytics-event], [data-testid]');
      if (target) {
        const identifier = target.getAttribute('data-analytics-event') || target.getAttribute('data-testid');
        if (!identifier || target.closest('form') || target.closest('[data-analytics-private="true"]')) return;
        trackEvent('click', {
          element_clicked: target.tagName.toLowerCase(),
          element_id: identifier.slice(0, 120),
        });
      }
    };

    document.addEventListener('click', handleClick, { passive: true });
    return () => document.removeEventListener('click', handleClick);
  }, [consentGranted, trackEvent]);

  // Track search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchQuery = params.get('q') || params.get('search') || params.get('query');
    if (consentGranted && searchQuery) {
      trackEvent('search', { search_query: searchQuery.slice(0, 160) });
    }
  }, [consentGranted, location.search, trackEvent]);

  // Expose tracking function globally for manual tracking
  useEffect(() => {
    window.rastyleAnalytics = {
      track: trackEvent,
      trackStream: (contentId, streamType) => {
        trackEvent('stream', { stream_content_id: contentId, stream_type: streamType });
      },
      trackStreamEnd: (contentId, duration) => {
        trackEvent('stream_end', { stream_content_id: contentId, stream_duration: duration });
      }
    };

    return () => {
      delete window.rastyleAnalytics;
    };
  }, [trackEvent]);

  return null; // This component doesn't render anything
};

export default AnalyticsTracker;
